import React, { Suspense, lazy } from 'react'
import { Redirect, Route, Switch } from 'Core'

// Components
import FallBack from 'Components/Fallback'
import CheckFeatureFlag from 'Views/CheckFeatureFlag'
import DeviceTabs from 'Views/DeviceTabs'
import IAMActionDetailsWithContext from 'Views/IamActionsAndResources/IAMActionList/IAMActionDetails'

// Feature Routes
import { ActivityLogsRoute } from 'features/activityLogs'
import { ApplicationRoutes } from 'features/applications'
import { ApprovalListRoutes } from 'features/approvals'
import { BundlesRoutes } from 'features/bundles'
import { CloudsRoutes } from 'features/clouds'
import { DashboardRoutes } from 'features/dashboard'
import { GatewayRoutes } from 'features/gateways'
import { TeamDetails } from 'features/github'
import { GroupsRoutes } from 'features/groups'
import { IAMResourcesRoutes } from 'features/iamResources'
import { IAMRolesRoutes } from 'features/iamRoles'
import { IdaRoutes } from 'features/IdentityAnalyzer'
import { IntegrationsRoutes } from 'features/integrations'
import {
  RedirectJiraSuccess,
  RedirectMSTeamSuccess,
  RedirectServiceNowSuccess,
  RedirectSlackSuccess
} from 'features/layout'
import { PolicyRoutes } from 'features/policy'
import { ProxyControllersRoutes } from 'features/proxyControllers'
import { ServerControllersRoutes } from 'features/serverControllers'
import { SessionRoutes } from 'features/sessions'
import { DevicePassKeys, SettingsRoute } from 'features/settings'
import { DataStoreRoutes } from 'features/snowflake'
import { TagsRoutes } from 'features/tags'
import { ADDomainControllers, CreateADD, TargetsRoutes } from 'features/targets'
import { UserRoutes } from 'features/users'
import { WorkloadsRoutes } from 'features/workloads'
import { ServiceAccounts } from 'features/servieAccounts'

// Lazy Loaded Components
const IAMActionAndResources = lazy(() => import('Views/IamActionsAndResources'))
const DeviceDetails = lazy(() => import('Views/DeviceList/DeviceDetails'))
const NotFound = lazy(() => import('Views/NotFound'))
const SlackLogin = lazy(() => import('features/slackLogin'))

const VIEW_PREFIX = 'admin'

const AdminviewRoutes = () => {
  return (
    <Suspense fallback={<FallBack />}>
      <Switch>
        {/* Root Redirects */}
        <Route exact path={[`/${VIEW_PREFIX}/`, `/${VIEW_PREFIX}/index.html`]}>
          <Redirect to={`/${VIEW_PREFIX}/dashboard`} />
        </Route>

        {/* Main Feature Routes */}
        <Route
          exact
          path={[`/${VIEW_PREFIX}/passkeys`, `/${VIEW_PREFIX}/passkeys/*`]}
          component={DevicePassKeys}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/gateways`, `/${VIEW_PREFIX}/gateways/*`]}
          component={GatewayRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/proxy-controllers`, `/${VIEW_PREFIX}/proxy-controllers/*`]}
          component={ProxyControllersRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/server-controllers`, `/${VIEW_PREFIX}/server-controllers/*`]}
          component={ServerControllersRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/tags`, `/${VIEW_PREFIX}/tags/*`]}
          component={TagsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/dashboard`, `/${VIEW_PREFIX}/dashboard/*`]}
          component={DashboardRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/bundles`, `/${VIEW_PREFIX}/bundles/*`]}
          component={BundlesRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/iam-roles`, `/${VIEW_PREFIX}/iam-roles/*`]}
          component={IAMRolesRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/activity-logs`, `/${VIEW_PREFIX}/activity-logs/*`]}
          component={ActivityLogsRoute}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/targets`, `/${VIEW_PREFIX}/targets/*`]}
          component={TargetsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/resources`, `/${VIEW_PREFIX}/resources/*`]}
          component={IAMResourcesRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/policys`, `/${VIEW_PREFIX}/policys/*`]}
          component={PolicyRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/applications`, `/${VIEW_PREFIX}/applications/*`]}
          component={ApplicationRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/datacloud`, `/${VIEW_PREFIX}/datacloud/*`]}
          component={DataStoreRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/users`, `/${VIEW_PREFIX}/users/*`]}
          component={UserRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/groups`, `/${VIEW_PREFIX}/groups/*`]}
          component={GroupsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/access-requests`, `/${VIEW_PREFIX}/access-requests/*`]}
          component={ApprovalListRoutes}
        />
        <Route
          exact
          path={[
            `/${VIEW_PREFIX}/settings`,
            `/${VIEW_PREFIX}/settings/*`,
            `/${VIEW_PREFIX}/settings/:tabName`
          ]}
          component={SettingsRoute}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/clouds`, `/${VIEW_PREFIX}/clouds/*`]}
          component={CloudsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/session-logs`, `/${VIEW_PREFIX}/session-logs/*`]}
          component={SessionRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/workloads`, `/${VIEW_PREFIX}/workloads/*`]}
          component={WorkloadsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/integrations`, `/${VIEW_PREFIX}/integrations/*`]}
          component={IntegrationsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/idaanalyzer`, `/${VIEW_PREFIX}/idaanalyzer/*`]}
          component={IdaRoutes}
        />

        {/* Active Directory Routes */}
        <Route exact path={`/${VIEW_PREFIX}/addomaincontrollers`} component={ADDomainControllers} />
        <Route
          exact
          path={`/${VIEW_PREFIX}/addomainControllers/:controllername`}
          component={CreateADD}
        />

        {/* IAM Action Routes with Feature Flags */}
        <Route exact path={`/${VIEW_PREFIX}/iam-actions/:accountType`}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='iam-actions' />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/iam-policy/:accountType`}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='iam-policy' />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/account-resources/:accountType`}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='account-resources' />
          </CheckFeatureFlag>
        </Route>

        <Route
          exact
          path={[`/${VIEW_PREFIX}/iam-action/:name`, `/${VIEW_PREFIX}/iam-action/:name/:action`]}
        >
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionDetailsWithContext />
          </CheckFeatureFlag>
        </Route>

        {/* Service Accounts Routes */}
        <Route
          exact
          path={[
            `/${VIEW_PREFIX}/service-accounts`,
            `/${VIEW_PREFIX}/service-accounts/:accountType`,
            `/${VIEW_PREFIX}/service-accounts/:accountType/:action`
          ]}
        >
          <CheckFeatureFlag isRoute permission='showServiceAccountsPage'>
            <ServiceAccounts />
          </CheckFeatureFlag>
        </Route>

        {/* Integration Redirect Routes */}
        <Route exact path={`/${VIEW_PREFIX}/slack/:integrationName`}>
          <CheckFeatureFlag isRoute permission='showIntegrationsPage'>
            <RedirectSlackSuccess />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/jira/:integrationName`}>
          <CheckFeatureFlag isRoute permission='showIntegrationsPage'>
            <RedirectJiraSuccess />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/servicenow/:integrationName`}>
          <CheckFeatureFlag isRoute permission='showIntegrationsPage'>
            <RedirectServiceNowSuccess />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/msteams/:integrationName`}>
          <CheckFeatureFlag isRoute permission='showIntegrationsPage'>
            <RedirectMSTeamSuccess />
          </CheckFeatureFlag>
        </Route>

        {/* Device Routes */}
        <Route exact path={`/${VIEW_PREFIX}/devices/approved`}>
          <CheckFeatureFlag isRoute permission='showDevicesPage'>
            <DeviceTabs />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/device/:id`}>
          <CheckFeatureFlag isRoute permission='showDeviceDetailsPage'>
            <DeviceDetails />
          </CheckFeatureFlag>
        </Route>

        {/* Team Details Route */}
        <Route exact path={`/${VIEW_PREFIX}/team-details/:teamName`}>
          <TeamDetails />
        </Route>

        {/* Slack Login Route */}
        <Route exact path={`/${VIEW_PREFIX}/slack-login`} component={SlackLogin} />

        {/* 404 - Must be last */}
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Suspense>
  )
}

export default AdminviewRoutes
